import 'react-grid-layout/css/styles.css';

import { forwardRef, useCallback, useMemo, useState } from 'react';
import {
  GridLayout,
  type Layout,
  type LayoutItem,
  useContainerWidth,
} from 'react-grid-layout';

import { useReport } from '../context/ReportContext';
import { ReportComponentHelper } from '../helpers/ReportComponentHelper';
import type { IReportItem } from '../types/report';
import { ComponentFrame } from './ComponentFrame';
import { OverrideDialog } from './OverrideDialog';

const GRID_MARGIN: [number, number] = [12, 12];

const samePosition = (item: IReportItem, layout: LayoutItem) =>
  layout.x === item.layout.x &&
  layout.y === item.layout.y &&
  layout.w === item.layout.w &&
  layout.h === item.layout.h;

/** Drag-and-resize grid of report components; accepts drops from the palette. */
export const ReportCanvas = forwardRef<HTMLDivElement>((_props, ref) => {
  const { items: allItems, setItems, columnLayout } = useReport();
  const items = useMemo(
    () => ReportComponentHelper.visibleItems(allItems, columnLayout),
    [allItems, columnLayout]
  );
  const { width, containerRef, mounted } = useContainerWidth({
    measureBeforeMount: true,
  });
  const [overrideTarget, setOverrideTarget] = useState<IReportItem | null>(
    null
  );

  const layout = useMemo<Layout>(
    () =>
      items.map((item) => {
        const { minSize } = ReportComponentHelper.definition(item.componentId);
        return {
          i: item.instanceId,
          ...item.layout,
          minW: minSize.w,
          minH: minSize.h,
        };
      }),
    [items]
  );

  const handleLayoutChange = useCallback(
    (next: Layout) => {
      setItems((prev) => {
        const updated = prev.map((item) => {
          const position = next.find((l) => l.i === item.instanceId);
          if (!position || samePosition(item, position)) return item;
          const { x, y, w, h } = position;
          return { ...item, layout: { x, y, w, h } };
        });
        return updated.some((item, i) => item !== prev[i]) ? updated : prev;
      });
    },
    [setItems]
  );

  return (
    <div ref={ref} className="min-h-full w-full">
      <div
        ref={containerRef}
        className="w-full [&_.react-grid-placeholder]:rounded-lg [&_.react-grid-placeholder]:!bg-bg-tertiary [&_.react-grid-placeholder]:!opacity-100 [&_.react-resizable-handle]:!bg-none [&_.react-resizable-handle::after]:!hidden"
      >
        {mounted && (
          <GridLayout
            width={width}
            layout={layout}
            onLayoutChange={handleLayoutChange}
            gridConfig={{
              cols: ReportComponentHelper.GRID_COLS,
              rowHeight: ReportComponentHelper.ROW_HEIGHT,
              margin: GRID_MARGIN,
              containerPadding: [0, 0],
            }}
            dragConfig={{
              enabled: true,
              handle: '.report-drag-handle',
              cancel: '[data-no-drag]',
            }}
            resizeConfig={{ enabled: true, handles: ['se'] }}
          >
            {items.map((item) => (
              <div key={item.instanceId}>
                <ComponentFrame item={item} onOverride={setOverrideTarget} />
              </div>
            ))}
          </GridLayout>
        )}
      </div>
      {items.length === 0 && (
        <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-border-secondary txt-small-regular text-text-tertiary">
          Turn on components from the settings menu to build your report.
        </div>
      )}
      <OverrideDialog
        item={overrideTarget}
        onClose={() => setOverrideTarget(null)}
      />
    </div>
  );
});

ReportCanvas.displayName = 'ReportCanvas';
